/**
 * StockStep Component
 * Step 4: Stock and observation fields for EPI (optional)
 *
 * @version 2025.1.0
 */

import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Grid,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import { CurrencyInput } from '../../../../../components/forms';
import { EpiFormData } from '../../../../../types/epi';
import { StockStepProps } from '../EPIFormWizard.types';

export const StockStep: React.FC<StockStepProps> = ({
  disabled = false,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<EpiFormData>();

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Informações opcionais de estoque e custo do EPI. Você pode pular esta etapa se não quiser preencher agora.
      </Typography>

      <Grid container spacing={3}>
        {/* Quantidade */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name="quantidade"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  field.onChange(value === '' ? null : Number(value));
                }}
                label="Quantidade em Estoque"
                type="number"
                fullWidth
                error={Boolean(errors.quantidade)}
                helperText={errors.quantidade?.message}
                disabled={disabled}
                inputProps={{ min: 0 }}
              />
            )}
          />
        </Grid>

        {/* Unidade */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name="unidade"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Unidade"
                fullWidth
                error={Boolean(errors.unidade)}
                helperText={errors.unidade?.message || 'Ex: Par, Unidade, Caixa'}
                disabled={disabled}
              />
            )}
          />
        </Grid>

        {/* Tamanho */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name="tamanho"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                label="Tamanho"
                fullWidth
                error={Boolean(errors.tamanho)}
                helperText={errors.tamanho?.message || 'Ex: P, M, G, GG, 38, 42'}
                disabled={disabled}
              />
            )}
          />
        </Grid>

        {/* Custo */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name="custo"
            control={control}
            render={({ field }) => (
              <CurrencyInput
                value={field.value ?? undefined}
                onChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
                label="Custo Unitário"
                fullWidth
                error={Boolean(errors.custo)}
                helperText={errors.custo?.message}
                disabled={disabled}
              />
            )}
          />
        </Grid>

        {/* Observação */}
        <Grid size={{ xs: 12 }}>
          <Controller
            name="observacao"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Observações"
                fullWidth
                multiline
                rows={4}
                error={Boolean(errors.observacao)}
                helperText={errors.observacao?.message || 'Informações adicionais sobre o EPI'}
                disabled={disabled}
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default StockStep;
